import { For, Show, type Component } from 'solid-js';
import { A, useLocation } from '@solidjs/router';
import { cn } from '~/shared/lib/cn';
import { ChevronsLeft, ChevronsRight } from 'lucide-solid';
import { makePersisted } from '@solid-primitives/storage';
import { createSignal } from 'solid-js';
import { NAV_GROUPS, NAV_ITEMS, navLabelOf, type NavGroup, type NavItem } from './nav-items';
import { useI18n } from '~/shared/lib/i18n';
import { useAuth } from '~/shared/api/auth-context';
import { menuEntry } from '~/shared/access/sections';

export const Sidebar: Component = () => {
  const { t } = useI18n();
  const { session } = useAuth();
  const [collapsed, setCollapsed] = makePersisted(createSignal(false), {
    name: 'sportpark.sidebar.collapsed',
    storage: localStorage,
  });
  const location = useLocation();

  const isActive = (item: NavItem): boolean => {
    if (item.href === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(item.href);
  };

  const labelOf = (item: NavItem): string => navLabelOf(item, session()?.user.roles ?? []);

  const entryOf = (href: string): string | null => menuEntry(href, session()?.user.permissions ?? []);
  const canSee = (href: string): boolean => entryOf(href) !== null;

  const visibleItems = (group: NavGroup) =>
    NAV_ITEMS.filter((i) => i.group === group && canSee(i.href));

  return (
    <aside
      class={cn(
        'glass-strong sticky top-4 h-screen ml-0 flex-shrink-0 overflow-hidden',
        'flex flex-col transition-[width,padding-left] duration-base ease-ios',
        collapsed() ? 'w-[72px] pl-0' : 'w-[240px] pl-[20px]',
      )}
      style={{
        'border-left': '0',
        'border-radius': '0 24px 24px 0',
      }}
    >
      <div
        class={cn(
          'flex items-center gap-3 h-14 border-b border-hairline transition-[padding] duration-base ease-ios',
          collapsed() ? 'justify-center px-0' : 'px-4',
        )}
      >
        <div
          class="w-8 h-8 rounded-sm flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg, var(--color-accent), var(--color-purple))',
          }}
          aria-hidden="true"
        />
        <Show when={!collapsed()}>
          <span class="font-[var(--font-display)] text-[16px] font-semibold tracking-tight">
            {t('app.title')}
          </span>
        </Show>
      </div>

      <nav class="flex-1 overflow-y-auto py-2 px-2 space-y-4">
        <For each={NAV_GROUPS}>
          {(group) => (
            <Show when={visibleItems(group.key).length > 0}>
              <div>
                <Show when={!collapsed()}>
                  <p class="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-tertiary">
                    {group.title}
                  </p>
                </Show>
                <ul class="space-y-0.5">
                  <For each={visibleItems(group.key)}>
                    {(item) => {
                      const shape = () => cn(
                        'press group relative flex items-center gap-3 px-3 h-10',
                        'rounded-md text-[14px]',
                        'text-secondary hover:text-primary',
                        'hover:bg-white/6 transition-all duration-base ease-ios',
                        isActive(item) && 'nav-active',
                        collapsed() ? 'justify-center' : '',
                      );

                      if (item.comingSoon) {
                        return (
                          <li>
                            <div
                              class={cn(shape(), 'opacity-55 cursor-default hover:bg-transparent')}
                              aria-disabled="true"
                              title={`${labelOf(item)} — ${item.comingSoon}`}
                            >
                              <item.icon class="h-[18px] w-[18px] flex-shrink-0" />
                              <Show when={!collapsed()}>
                                <span class="truncate">{labelOf(item)}</span>
                                <span
                                  class="ml-auto shrink-0 px-1.5 py-[1px] rounded-full text-[10px] font-medium"
                                  style={{
                                    background:
                                      'color-mix(in srgb, var(--color-text-tertiary) 16%, transparent)',
                                    color: 'var(--color-text-tertiary)',
                                  }}
                                >
                                  Скоро
                                </span>
                              </Show>
                            </div>
                          </li>
                        );
                      }

                      return (
                        <li>
                          <A
                            href={entryOf(item.href) ?? item.href}
                            class={shape()}
                            title={collapsed() ? labelOf(item) : undefined}
                          >
                            <item.icon class="h-[18px] w-[18px] flex-shrink-0" />
                            <Show when={!collapsed()}>
                              <span class="truncate">{labelOf(item)}</span>
                            </Show>
                          </A>
                        </li>
                      );
                    }}
                  </For>
                </ul>
              </div>
            </Show>
          )}
        </For>
      </nav>

      <button
        type="button"
        class="press flex items-center justify-center gap-2 h-10 mx-2 mb-3 rounded-md text-[13px] text-secondary hover:bg-white/6"
        onClick={() => setCollapsed((v) => !v)}
        aria-label={collapsed() ? t('nav.expand') : t('nav.collapse')}
      >
        <Show when={collapsed()} fallback={<><ChevronsLeft class="h-4 w-4" /> {t('nav.collapse')}</>}>
          <ChevronsRight class="h-4 w-4" />
        </Show>
      </button>

      <style>{`
        .nav-active {
          color: var(--color-text-primary);
          background: linear-gradient(90deg, hsl(211 100% 55% / 0.14), transparent 75%);
          box-shadow: inset 3px 0 0 var(--color-accent);
        }
      `}</style>
    </aside>
  );
};
